/**
 * <legal-tabs> - vertical tab list (desktop) / custom dropdown (mobile) that switches which
 * page's content shows on the right. Scoped entirely to its own element, so multiple instances of
 * the legal-tabs section can coexist on one page without colliding.
 */
class LegalTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.dropdown = this.querySelector('.legal-tabs__dropdown');
    this.dropdownToggle = this.querySelector('.legal-tabs__dropdown-toggle');
    this.dropdownValue = this.querySelector('.legal-tabs__dropdown-value');
    this.dropdownLabel = this.querySelector('.legal-tabs__dropdown-label');
    this.dropdownList = this.querySelector('.legal-tabs__dropdown-list');
    this.dropdownOptions = Array.from(this.querySelectorAll('.legal-tabs__dropdown-option'));
    this.breadcrumbCurrent = this.querySelector('.legal-tabs__breadcrumb-current');

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectIndex(index));
    });

    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.dropdownToggle) {
      this.dropdownToggle.addEventListener('click', () => this.toggleDropdown());
    }

    this.dropdownOptions.forEach((option, index) => {
      option.addEventListener('click', () => {
        this.selectIndex(index);
        this.closeDropdown();
        this.dropdownToggle.focus();
      });
    });

    if (this.dropdown) {
      this.dropdown.addEventListener('keydown', this.onDropdownKeydown.bind(this));
      document.addEventListener('click', (event) => {
        if (!this.dropdown.contains(event.target)) {
          this.closeDropdown();
        }
      });
    }

    // Keep in sync with the theme editor: selecting a "Legal page" block should reveal its
    // (possibly hidden) panel, the same way the theme's built-in <x-tabs> component does.
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.tabs.indexOf(event.target);

      if (index !== -1) {
        this.selectIndex(index);
      }
    });

    this.selectFromUrl();
  }

  selectFromUrl() {
    const tabParam = new URLSearchParams(window.location.search).get('tab');

    if (!tabParam) {
      return;
    }

    const normalizedParam = tabParam.trim().toLowerCase();
    const index = this.tabs.findIndex((tab) => {
      return (
        tab.dataset.tabParam?.toLowerCase() === normalizedParam ||
        tab.dataset.tabHandle?.toLowerCase() === normalizedParam ||
        tab.dataset.tabTitleHandle?.toLowerCase() === normalizedParam
      );
    });

    if (index !== -1) {
      this.selectIndex(index, { updateUrl: false });
    }
  }

  selectIndex(index, { updateUrl = true } = {}) {
    if (index < 0 || index >= this.tabs.length) {
      return;
    }

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.classList.toggle('is-selected', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.dropdownOptions.forEach((option, i) => {
      const selected = i === index;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    if (this.dropdownValue && this.dropdownOptions[index]) {
      this.dropdownValue.textContent = this.dropdownOptions[index].textContent.trim();
    }

    if (this.breadcrumbCurrent && this.tabs[index]) {
      this.breadcrumbCurrent.textContent = this.tabs[index].textContent.trim();
    }

    if (updateUrl) {
      const tabHandle = this.tabs[index]?.dataset.tabParam || this.tabs[index]?.dataset.tabHandle;

      if (tabHandle) {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', tabHandle);
        window.history.replaceState(window.history.state, '', url);
      }
    }
  }

  toggleDropdown() {
    if (!this.dropdown) {
      return;
    }

    const isOpen = this.dropdownToggle.getAttribute('aria-expanded') === 'true';
    isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdownToggle.setAttribute('aria-expanded', 'true');
    this.dropdownList.hidden = false;
    this.dropdown.classList.add('is-open');

    const selected = this.dropdownOptions.find((option) => option.classList.contains('is-selected'));
    (selected || this.dropdownOptions[0])?.focus();
  }

  closeDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdownToggle.setAttribute('aria-expanded', 'false');
    this.dropdownList.hidden = true;
    this.dropdown.classList.remove('is-open');
  }

  onDropdownKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDropdown();
      this.dropdownToggle.focus();
      return;
    }

    if (!['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
      return;
    }

    const active = this.dropdownOptions.indexOf(document.activeElement);

    if (active === -1) {
      return;
    }

    event.preventDefault();

    if (event.key === 'Enter' || event.key === ' ') {
      this.selectIndex(active);
      this.closeDropdown();
      this.dropdownToggle.focus();
      return;
    }

    let nextIndex = active;

    if (event.key === 'ArrowUp') {
      nextIndex = (active - 1 + this.dropdownOptions.length) % this.dropdownOptions.length;
    } else if (event.key === 'ArrowDown') {
      nextIndex = (active + 1) % this.dropdownOptions.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.dropdownOptions.length - 1;
    }

    this.dropdownOptions[nextIndex].focus();
  }

  onKeydown(event) {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const active = this.tabs.indexOf(document.activeElement);

    if (active === -1) {
      return;
    }

    event.preventDefault();

    let nextIndex = active;

    if (event.key === 'ArrowUp') {
      nextIndex = (active - 1 + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'ArrowDown') {
      nextIndex = (active + 1) % this.tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1;
    }

    this.selectIndex(nextIndex);
    this.tabs[nextIndex].focus();
  }
}

if (!window.customElements.get('legal-tabs')) {
  window.customElements.define('legal-tabs', LegalTabs);
}

/*
 * "Back to account" link above the Legal heading. It's rendered hidden and only revealed when the
 * customer arrived from one of the store's account pages (/account, /account/addresses, ...), since
 * Liquid has no access to the referrer. Clicking it goes back in history so the customer lands on the
 * exact account page/tab they left; the href (account root) is the fallback if history is unavailable.
 */
const cameFromAccountPage = (accountUrl) => {
  try {
    if (document.referrer === '') {
      return false;
    }

    const referrer = new URL(document.referrer);

    return referrer.origin === window.location.origin && referrer.pathname.startsWith(accountUrl);
  } catch (error) {
    return false;
  }
};

document.querySelectorAll('[data-legal-back-link]').forEach((link) => {
  if (cameFromAccountPage(link.dataset.accountUrl)) {
    link.closest('.legal-tabs__back')?.removeAttribute('hidden');
  }
});

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-legal-back-link]');

  if (!link) {
    return;
  }

  if (cameFromAccountPage(link.dataset.accountUrl) && window.history.length > 1) {
    event.preventDefault();
    window.history.back();
  }
});
